import { useState } from 'react'
import { toast } from 'sonner'
import { Dices, Radio, BarChart3, FlaskConical, Calculator, Play, Square, Paperclip, Lock, Target } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer'
import { cn } from '@/lib/utils'
import { CalculatorSheet } from './CalculatorSheet'
import { PedirTesteDialog } from './PedirTesteDialog'

interface QuickAction {
  icon: typeof Dices
  label: string
  /** O que a linha faz, em uma frase. Fica sob o rótulo. */
  description: string
  action: () => void
  color: string
  bgColor: string
  hoverBgColor: string
  /** Id do módulo em table_modules; ausente = ação fixa (sempre visível) */
  moduleId?: string
  /**
   * A linha aparece, mas não responde — e diz por quê (2026-07-27).
   *
   * O bloqueio desceu do BOTÃO para cada linha (decisão 13 do
   * docs/PLANO_AREA_DO_CHAT.md). Antes, um único cadeado no raio trancava o
   * painel inteiro para o aluno fora da vez dele: a calculadora ficava
   * inacessível porque não era a vez de ninguém falar. Aqui, cada linha
   * responde pela própria regra.
   */
  motivoDoBloqueio?: string
}

interface QuickActionsBottomSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onRollDice?: () => void
  onLiveSync?: () => void
  onPoll?: () => void
  onLab?: () => void
  disabled?: boolean
  isMaster?: boolean
  /** Ids dos módulos ativados na mesa (gating — regra 2026-07-23:
   * ação de módulo só aparece se o mestre ativou o módulo no gerenciador) */
  enabledModules?: string[]
  /** Leva a conta da calculadora para a caixa de mensagem (não envia sozinho) */
  onLevarContaParaChat?: (texto: string) => void
  /**
   * Abre o seletor de arquivos do sistema (2026-07-27). O clipe saiu da fileira
   * da caixa de mensagem e virou uma linha daqui — **uma** linha, e não as três
   * de antes (Galeria / Tirar Foto / Gravar Vídeo): no celular o seletor do
   * próprio sistema já oferece câmera e galeria, e no computador as duas
   * últimas nunca fizeram nada.
   */
  onEnviarMidia?: () => void
  /**
   * Mídia liberada? `false` durante a vez de outra pessoa.
   *
   * **Não é o mesmo que "pode rolar dados"**, e confundir os dois é o erro
   * fácil desta fase. Sem NENHUMA rodada aberta o aluno pode mandar foto (e
   * podia antes desta mudança) — tirar isso dele seria regressão silenciosa.
   */
  podeEnviarMidia?: boolean
  /**
   * Sessão da missão ativa (2026-07-27): iniciar e encerrar, que viviam na
   * `MissionSessionBar` — uma barra permanente na coluna do chat. Este painel
   * é o receptáculo do que sai da moldura (decisão 8 do
   * docs/PLANO_AREA_DO_CHAT.md). `null` sem missão ativa.
   */
  sessaoDaMissao?: {
    aberta: boolean
    onIniciar: () => Promise<void>
    onEncerrar: () => Promise<void>
  } | null
  /**
   * Pedir teste de atributo a quem tem a vez (fase 1 de
   * docs/PLANO_ROLAGEM_CLASSICA.md). Ausente = a linha não aparece.
   *
   * É a segunda metade do "Rolar Dados": aquele abre a bandeja livre, este
   * NOMEIA o desafio antes do dado — o ciclo clássico de RPG. As duas linhas
   * convivem de propósito; a bandeja livre continua sendo a ferramenta.
   */
  pedidoDeTeste?: {
    tableId: string
    /** Nome de quem tem a vez, para a linha dizer a quem o teste vai */
    nomeDaVez: string | null
    /** Por que não dá para convocar agora (ausente = pode) */
    motivoDoBloqueio?: string
  } | null
  trigger?: React.ReactNode
}

/**
 * O painel de ações rápidas — o receptáculo do que sai da moldura do chat.
 *
 * Até 2026-07-27 o raio que o abre tinha um cadeado único
 * (`isMaster || (rodada aberta && minha vez)`) e o painel inteiro ficava fora
 * do alcance do aluno na maior parte da aula — inclusive a calculadora, que não
 * tem nada a ver com turno. **O bloqueio desceu do botão para cada linha**
 * (decisão 13 do docs/PLANO_AREA_DO_CHAT.md), e as condições são DIFERENTES:
 *
 * | Linha                    | Quando aparece / responde                    |
 * |--------------------------|----------------------------------------------|
 * | Rolar Dados              | só o mestre (decisão 9), módulo `dice` ativo |
 * | Foto ou vídeo            | todos; bloqueada na vez de outra pessoa      |
 * | Calculadora              | todos, sempre; módulo `calculator` ativo     |
 * | Iniciar/Encerrar sessão  | só o mestre, e só com missão ativa           |
 * | Sincronização / Enquete / Aula | só o mestre, módulo ativo              |
 *
 * **Não unifique as duas primeiras condições.** `podeEnviarMidia` vem do
 * `!isTurnDisabled` (`rodada aberta && não é minha vez`), que **não é a
 * negação** do antigo `canUseQuickActions`: com NENHUMA rodada aberta os dois
 * divergem — a mídia funciona e o dado não. Se a mídia herdasse o gate do dado,
 * o aluno perderia algo que tem hoje: mandar foto numa mesa sem rodada aberta.
 */
export function QuickActionsBottomSheet({
  open,
  onOpenChange,
  onRollDice,
  onLiveSync,
  onPoll,
  onLab,
  disabled = false,
  isMaster = false,
  enabledModules = [],
  onLevarContaParaChat,
  onEnviarMidia,
  podeEnviarMidia = true,
  sessaoDaMissao,
  pedidoDeTeste,
  trigger
}: QuickActionsBottomSheetProps) {
  // Calculadora (modulo "calculator"): 100% client-side, aberta daqui mesmo
  const [showCalculator, setShowCalculator] = useState(false)
  // Pedir teste: mesma casa da calculadora — o painel abre o proprio dialogo
  const [showPedirTeste, setShowPedirTeste] = useState(false)
  const [ocupado, setOcupado] = useState(false)

  const alternarSessaoDaMissao = async () => {
    if (!sessaoDaMissao || ocupado) return
    setOcupado(true)
    try {
      if (sessaoDaMissao.aberta) {
        await sessaoDaMissao.onEncerrar()
        toast.success('Sessão encerrada')
      } else {
        await sessaoDaMissao.onIniciar()
        toast.success('Sessão iniciada')
      }
    } catch (erro: any) {
      toast.error(erro?.message || 'Erro ao mudar a sessão da missão')
    } finally {
      setOcupado(false)
    }
  }

  const quickActions: QuickAction[] = [
    {
      icon: Dices,
      label: 'Rolar Dados',
      description: 'Rolar dados de jogo',
      action: onRollDice || (() => { }),
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10',
      hoverBgColor: 'hover:bg-purple-500/20',
      // Modulo "dice": nem todo jogo usa dado (docs/PLANO_MODULO_DADOS.md).
      // Some quando o mestre desliga — e, desde 2026-07-27, e linha SO DO
      // MESTRE (decisao 9): nao faz sentido rolar dado se o mestre nao pedir.
      // Quem abre a bandeja e ele, que convoca quem rola; o aluno recebe a
      // bandeja aberta e executa. Some do painel do aluno em vez de ficar
      // trancada — linha eternamente bloqueada e ruido.
      moduleId: 'dice'
    },
    // Pedir teste: a outra metade da rolagem (fase 1 da rolagem classica). O
    // "Rolar Dados" acima abre a bandeja livre — util, mas sem alvo a bater.
    // Aqui o mestre NOMEIA o desafio ("teste Agilidade, dificuldade 12") e a
    // fila do turno diz a quem ele cabe; o veredito sai publico no chat.
    ...(pedidoDeTeste ? [{
      icon: Target,
      label: 'Pedir teste',
      description: pedidoDeTeste.nomeDaVez
        ? `Convocar ${pedidoDeTeste.nomeDaVez} a testar um atributo`
        : 'Testar um atributo de quem tem a vez',
      action: () => setShowPedirTeste(true),
      color: 'text-amber-400',
      bgColor: 'bg-amber-500/10',
      hoverBgColor: 'hover:bg-amber-500/20',
      moduleId: 'dice',
      motivoDoBloqueio: pedidoDeTeste.motivoDoBloqueio,
    }] : []),
    // Foto ou video: veio do clipe que morava na fileira da caixa de mensagem
    // (2026-07-27), devolvendo ~44px de largura ao campo de digitacao.
    ...(onEnviarMidia ? [{
      icon: Paperclip,
      label: 'Foto ou vídeo',
      description: 'Da galeria ou da câmera',
      action: onEnviarMidia,
      color: 'text-green-400',
      bgColor: 'bg-green-500/10',
      hoverBgColor: 'hover:bg-green-500/20',
      motivoDoBloqueio: podeEnviarMidia ? undefined : 'Na sua vez',
    }] : []),
    // Sessao da missao: so aparece com missao ativa, e so para o mestre (o
    // filtro so-mestre abaixo). Veio da MissionSessionBar em 2026-07-27.
    ...(sessaoDaMissao ? [{
      icon: sessaoDaMissao.aberta ? Square : Play,
      label: sessaoDaMissao.aberta ? 'Encerrar sessão' : 'Iniciar sessão',
      description: sessaoDaMissao.aberta
        ? 'Fechar o capítulo em andamento'
        : 'Abrir o capítulo desta missão',
      action: alternarSessaoDaMissao,
      color: 'text-teal-400',
      bgColor: 'bg-teal-500/10',
      hoverBgColor: 'hover:bg-teal-500/20',
    }] : []),
    {
      icon: Radio,
      label: 'Sincronização',
      description: 'Compartilhar conteúdo síncrono',
      action: onLiveSync || (() => { }),
      color: 'text-orange-400',
      bgColor: 'bg-orange-500/10',
      hoverBgColor: 'hover:bg-orange-500/20',
      moduleId: 'livesync'
    },
    {
      icon: BarChart3,
      label: 'Enquete',
      description: 'Criar enquete para a turma',
      action: onPoll || (() => { }),
      color: 'text-violet-400',
      bgColor: 'bg-violet-500/10',
      hoverBgColor: 'hover:bg-violet-500/20',
      moduleId: 'poll'
    },
    {
      icon: FlaskConical,
      label: 'Aula',
      description: 'Iniciar aula interativa em tela cheia',
      action: onLab || (() => { }),
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-500/10',
      hoverBgColor: 'hover:bg-emerald-500/20',
      moduleId: 'module_lab'
    },
    {
      icon: Calculator,
      label: 'Calculadora',
      description: 'Contas rápidas sem sair da mesa',
      action: () => setShowCalculator(true),
      color: 'text-sky-400',
      bgColor: 'bg-sky-500/10',
      hoverBgColor: 'hover:bg-sky-500/20',
      moduleId: 'calculator'
    }
  ]
    .filter(a => !a.moduleId || enabledModules.includes(a.moduleId))
    // Calculadora e "Foto ou video" sao para TODOS. Todo o resto — inclusive
    // "Rolar Dados", desde 2026-07-27 — e so do mestre.
    .filter(a => (
      a.label === 'Calculadora' || a.label === 'Foto ou vídeo' || isMaster
    ))

  const handleAction = (action: () => void) => {
    action()
    onOpenChange(false)
  }

  return (
    <>
    <Drawer open={open} onOpenChange={onOpenChange}>
      {trigger && (
        <DrawerTrigger asChild>
          {trigger}
        </DrawerTrigger>
      )}
      <DrawerContent
        className="bg-game-secondary border-t border-game-primary max-h-[85vh]"
        data-vaul-drawer-direction="bottom"
      >
        <DrawerHeader className="text-left pb-3 pt-2">
          <DrawerTitle className="text-game-primary text-xl font-semibold">
            Ações Rápidas
          </DrawerTitle>
          <p className="text-game-secondary text-sm mt-1">
            Escolha uma ação para executar
          </p>
        </DrawerHeader>

        <div className="px-4 pb-8 space-y-2.5">
          {quickActions.map((action, index) => {
            const bloqueada = Boolean(action.motivoDoBloqueio)
            const inerte = disabled || bloqueada
            // Linha bloqueada troca o icone pelo cadeado e a descricao pela
            // RAZAO. A diferenca entre ficar muda e dizer por que: "Na sua vez"
            // ensina a regra do jogo; um botao cinza so frustra.
            const Icon = bloqueada ? Lock : action.icon
            return (
              <Button
                key={index}
                variant="ghost"
                onClick={() => { if (!bloqueada) handleAction(action.action) }}
                disabled={inerte}
                aria-disabled={inerte}
                data-testid={`acao-${action.label}`}
                className={cn(
                  "w-full h-16 justify-start text-left px-4 rounded-xl transition-all duration-300",
                  "border border-game-primary group",
                  action.bgColor,
                  !inerte && action.hoverBgColor,
                  !inerte && "hover:scale-[1.02] hover:shadow-lg hover:shadow-black/20 active:scale-[0.98]",
                  inerte && "opacity-50 cursor-not-allowed hover:scale-100"
                )}
              >
                <div className={cn(
                  "w-12 h-12 rounded-xl flex items-center justify-center mr-4 transition-all duration-300",
                  action.bgColor,
                  "border border-game-primary",
                  !inerte && "group-hover:scale-110 group-hover:shadow-md"
                )}>
                  <Icon className={cn("h-6 w-6", bloqueada ? "text-game-muted" : action.color)} />
                </div>
                <div className="flex-1">
                  <div className={cn("font-semibold text-base", bloqueada ? "text-game-muted" : action.color)}>
                    {action.label}
                  </div>
                  <div className="text-game-secondary text-xs mt-0.5">
                    {action.motivoDoBloqueio ?? action.description}
                  </div>
                </div>
              </Button>
            )
          })}
        </div>
      </DrawerContent>
    </Drawer>
    <CalculatorSheet
      open={showCalculator}
      onOpenChange={setShowCalculator}
      onLevarParaChat={onLevarContaParaChat}
    />
    {pedidoDeTeste && (
      <PedirTesteDialog
        open={showPedirTeste}
        onOpenChange={setShowPedirTeste}
        tableId={pedidoDeTeste.tableId}
        // Sem ficha: o alvo e' a VEZ, e quem a resolve e' o servidor no
        // instante do pedido — a vez pode virar enquanto o dialogo esta aberto.
        character={null}
        alvoDaVez={pedidoDeTeste.nomeDaVez}
      />
    )}
    </>
  )
}
